// magnifier_controller.js
import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
    static targets = ["image"]
    static values = {
        minZoom: { type: Number, default: 1 },
        maxZoom: { type: Number, default: 4 },
        zoomStep: { type: Number, default: 0.25 }
    }

    connect() {
        this.scale = 1
        this.posX = 0
        this.posY = 0
        this.startX = 0
        this.startY = 0
        this.isDragging = false
        
        // Initialize
        this.setupZoomContainer()
        this.setupEventListeners()
        
        // Add CSS class when connected
        this.element.classList.add('magnifier-connected')
    }
    
    setupZoomContainer() {
        // Find the pages container
        this.pagesContainer = this.element.querySelector('.document-app__pages-container')
        
        // If pages container doesn't exist (single doc view) find the A4 container
        if (!this.pagesContainer) {
            this.pagesContainer = this.element.querySelector('.document-app__a4-container')
        }
        
        // If neither exists, find any image container
        if (!this.pagesContainer) {
            this.pagesContainer = this.element.querySelector('.document-app__image-wrapper')
        }
        
        // Use the found container as our zoom target
        if (this.pagesContainer) {
            // Set the container as our zoom container
            this.zoomContainer = this.pagesContainer
            
            // Set initial styles
            Object.assign(this.zoomContainer.style, {
                transform: `scale(${this.scale}) translate(${this.posX}px, ${this.posY}px)`,
                transition: 'transform 0.2s ease-out',
                transformOrigin: 'center center',
                cursor: 'grab'
            })
            
            // Create zoom indicator if it doesn't exist
            if (!this.element.querySelector('.zoom-indicator')) {
                this.zoomIndicator = document.createElement('div')
                this.zoomIndicator.classList.add('zoom-indicator')
                this.zoomIndicator.innerHTML = `${Math.round(this.scale * 100)}%`
                Object.assign(this.zoomIndicator.style, {
                    position: 'absolute',
                    bottom: '10px',
                    right: '10px',
                    background: 'rgba(0, 0, 0, 0.6)',
                    color: 'white',
                    padding: '3px 8px',
                    borderRadius: '12px',
                    fontSize: '12px',
                    fontWeight: 'bold',
                    opacity: '0',
                    transition: 'opacity 0.3s ease',
                    zIndex: '1000'
                })
                
                // Add the indicator to the parent of zoom container
                const previewContainer = this.element.querySelector('.document-app__preview')
                if (previewContainer) {
                    previewContainer.appendChild(this.zoomIndicator)
                } else {
                    // Fallback to the zoom container's parent
                    this.zoomContainer.parentNode.appendChild(this.zoomIndicator)
                }
            } else {
                this.zoomIndicator = this.element.querySelector('.zoom-indicator')
            }
        } else {
            console.warn('Magnifier: No suitable container found for zooming')
        }
    }
    
    setupEventListeners() {
        // Mouse drag events
        this.zoomContainer.addEventListener('mousedown', this.startDrag.bind(this))
        document.addEventListener('mousemove', this.dragMove.bind(this))
        document.addEventListener('mouseup', this.endDrag.bind(this))
        
        // Touch events for mobile
        this.zoomContainer.addEventListener('touchstart', this.startDrag.bind(this))
        document.addEventListener('touchmove', this.dragMove.bind(this))
        document.addEventListener('touchend', this.endDrag.bind(this))
        
        // Mousewheel zoom
        this.zoomContainer.addEventListener('wheel', this.handleWheel.bind(this))
        
        // Double-click to reset
        this.zoomContainer.addEventListener('dblclick', this.resetZoom.bind(this))
    }
    
    zoomIn(event) {
        if (event) event.preventDefault()
        this.updateZoom(this.scale + this.zoomStepValue)
    }
    
    zoomOut(event) {
        if (event) event.preventDefault()
        this.updateZoom(this.scale - this.zoomStepValue)
    }
    
    updateZoom(newScale, centerX = null, centerY = null) {
        // Constrain zoom level
        newScale = Math.max(this.minZoomValue, Math.min(this.maxZoomValue, newScale))
        
        // If no change, exit
        if (newScale === this.scale) return
        
        // Save previous scale for relative position calculation
        const prevScale = this.scale
        
        // Update scale
        this.scale = newScale
        
        // Update indicator
        this.zoomIndicator.innerHTML = `${Math.round(this.scale * 100)}%`
        this.showZoomIndicator()
        
        // Apply transform
        this.applyTransform()
    }
    
    startDrag(event) {
        event.preventDefault()
        this.isDragging = true
        
        // Get event details
        const clientX = event.clientX || (event.touches && event.touches[0].clientX)
        const clientY = event.clientY || (event.touches && event.touches[0].clientY)
        
        // Save starting positions
        this.startX = clientX - this.posX
        this.startY = clientY - this.posY
        
        // Change cursor style while dragging
        this.zoomContainer.style.cursor = 'grabbing'
    }
    
    dragMove(event) {
        if (!this.isDragging) return
        
        // Get event details
        const clientX = event.clientX || (event.touches && event.touches[0].clientX)
        const clientY = event.clientY || (event.touches && event.touches[0].clientY)
        
        // Calculate new position
        this.posX = clientX - this.startX
        this.posY = clientY - this.startY
        
        // Apply transform without transition for smoother dragging
        this.zoomContainer.style.transition = 'none'
        this.applyTransform()
    }
    
    endDrag() {
        this.isDragging = false
        this.zoomContainer.style.cursor = 'grab'
        this.zoomContainer.style.transition = 'transform 0.2s ease-out'
    }
    
    handleWheel(event) {
        event.preventDefault()
        
        // Zoom in or out based on wheel direction
        if (event.deltaY < 0) {
            this.zoomIn()
        } else {
            this.zoomOut()
        }
    }
    
    resetZoom(event) {
        if (event) event.preventDefault()
        
        this.scale = 1
        this.posX = 0
        this.posY = 0
        
        this.zoomIndicator.innerHTML = '100%'
        this.showZoomIndicator()
        
        this.applyTransform()
    }
    
    applyTransform() {
        this.zoomContainer.style.transform = `scale(${this.scale}) translate(${this.posX / this.scale}px, ${this.posY / this.scale}px)`
    }
    
    showZoomIndicator() {
        // Show the indicator
        this.zoomIndicator.style.opacity = '1'
        
        // Clear any existing timeout
        if (this.indicatorTimeout) {
            clearTimeout(this.indicatorTimeout)
        }
        
        // Hide after 1.5 seconds
        this.indicatorTimeout = setTimeout(() => {
            this.zoomIndicator.style.opacity = '0'
        }, 1500)
    }
    
    disconnect() {
        // Clean up event listeners
        document.removeEventListener('mousemove', this.dragMove.bind(this))
        document.removeEventListener('mouseup', this.endDrag.bind(this))
        document.removeEventListener('touchmove', this.dragMove.bind(this))
        document.removeEventListener('touchend', this.endDrag.bind(this))
        
        // Remove the indicator timeout
        if (this.indicatorTimeout) {
            clearTimeout(this.indicatorTimeout)
        }
    }
}